import React, { useState, useEffect, useContext } from 'react';
import { createRoot } from 'react-dom/client';
import { DayPicker, type MonthCaptionProps } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { enUS } from 'date-fns/locale/en-US';
import { fr } from 'date-fns/locale/fr';
import GetLabel from "../language/GetLabel.tsx";
import { getLanguage } from "../language/lang-utils.ts";
import { Language } from "../language/Language.ts";

type Props = {
  localeCode?: string;
  inputName: string;
  initialValue?: string | Date;
  id?: string;
};

export const isFutureDate = (date?: Date | null) => {
  if (!(date instanceof Date) || isNaN(date.getTime())) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const candidate = new Date(date);
  candidate.setHours(0, 0, 0, 0);

  return candidate > today;
};

const formatToServerString = (date?: Date | null) => {
  if (!date) return "";
  // Format as local yyyy-mm-dd to avoid timezone shifts when round-tripping
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const parseDateInput = (value: string) => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return undefined;

  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return date.getFullYear() === Number(match[1]) &&
    date.getMonth() === Number(match[2]) - 1 &&
    date.getDate() === Number(match[3])
    ? date
    : undefined;
};

type DateInputContextValue = {
  value: string;
  inputId: string;
  onChange: (value: string) => void;
  placeholderDateFormat: string;
};

const DateInputContext = React.createContext<DateInputContextValue | undefined>(undefined);

const MonthCaptionWithInput = (captionProps: MonthCaptionProps) => {
  const dateInputContext = useContext(DateInputContext);

  return (
    <div {...captionProps}>
      <label htmlFor={dateInputContext?.inputId}>{dateInputContext?.inputId === "startDate-date-input" ? <GetLabel name={"START_DATE"}/> : <GetLabel name={"END_DATE"}/>}</label>
      <input
        type="text"
        id={dateInputContext?.inputId}
        value={dateInputContext?.value || ''}
        placeholder={dateInputContext?.placeholderDateFormat ?? "yyyy-mm-dd"}
        pattern="\\d{4}-\\d{2}-\\d{2}"
        inputMode="numeric"
        aria-label="Date"
        onChange={(event) => dateInputContext?.onChange(event.target.value)}
      />
      {captionProps.children}
    </div>
  );
};

const DatePickerComponent: React.FC<Props> = ({ localeCode, inputName, initialValue, id }) => {
  // Parse an initial value of form yyyy-mm-dd into a local Date (avoid UTC parsing)
  const parseInitial = (v?: string | Date) => {
    if (!v) return undefined;
    if (v instanceof Date) return v;
    const s = String(v);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      const year = parseInt(m[1], 10);
      const month = parseInt(m[2], 10) - 1;
      const day = parseInt(m[3], 10);
      return new Date(year, month, day);
    }
    // fallback to Date constructor
    const d = new Date(s);
    return isNaN(d.getTime()) ? undefined : d;
  };
  const parsedInitial = parseInitial(initialValue);
  const [selected, setSelected] = useState<Date | undefined>(parsedInitial);
  const [month, setMonth] = useState<Date>(parsedInitial || new Date());
  const [dateInput, setDateInput] = useState(formatToServerString(parsedInitial));
  const [dateFnsLocale, setDateFnsLocale] = useState<any>(null);
  const placeholderDateFormat = localeCode?.toLowerCase().startsWith('fr') || getLanguage() === Language.fr ? "aaaa-mm-jj" : "yyyy-mm-dd";

  useEffect(() => {
    if (!localeCode || localeCode === 'en') {
      setDateFnsLocale(enUS);
    } else if (localeCode.startsWith('fr')) {
      setDateFnsLocale(fr);
    } else {
      // fallback to en
      setDateFnsLocale(enUS);
    }
  }, [localeCode]);

  // Only notify outer JS when the user actually selects a date (avoid firing on mount)
  const handleSelect = (date?: Date | null) => {
    if (isFutureDate(date)) {
      return;
    }

    setSelected(date ?? undefined);
    setDateInput(formatToServerString(date));
    if (date instanceof Date && !isNaN(date.getTime())) {
      const ev = new CustomEvent('cpac:dateChanged', { detail: { name: inputName, value: formatToServerString(date) }, bubbles: true });
      // dispatch at document level so listeners can pick it up
      document.dispatchEvent(ev);
    }
  };

  const handleDateInputChange = (value: string) => {
    setDateInput(value);
    const date = parseDateInput(value);
    if (!date || isFutureDate(date)) return;

    setMonth(date);
    handleSelect(date);
  };

  return (
    <div id={id} className="date-picker-wrapper">
      <DateInputContext.Provider value={{ value: dateInput, inputId: `${inputName}-date-input`, onChange: handleDateInputChange, placeholderDateFormat }}>
        <DayPicker
          mode="single"
          selected={selected}
          onSelect={handleSelect}
          month={month}
          onMonthChange={setMonth}
          disabled={isFutureDate}
          locale={dateFnsLocale || undefined}
          components={{ MonthCaption: MonthCaptionWithInput }}
          labels={{
          // Shorten the next/previous button copy
            labelPrevious: () => dateFnsLocale == fr ? "Mois précédent" : "Previous month",
            labelNext: () => dateFnsLocale == fr ? "Mois suivant" : "Next month"
          }}
        />
      </DateInputContext.Provider>
      <input
        type="hidden"
        id={`${inputName}-value`}
        name={inputName}
        value={formatToServerString(selected)}
        aria-hidden="true"
      />
    </div>
  );
};

// Find placeholders and mount date pickers automatically
export const renderDatePickers = () => {
  if (typeof document === 'undefined') return;
  const els = document.querySelectorAll<HTMLElement>('.react-date-picker');
  els.forEach((el) => {
    const inputName = el.dataset['inputName'] || el.id || '';
    const initialValue = el.dataset['initialValue'] || '';
    const locale = el.dataset['locale'] || undefined;
    const itemId = 'react-' + el.id;
    try {
      const root = createRoot(el);
      root.render(
        React.createElement(DatePickerComponent, {
          localeCode: locale,
          inputName,
          initialValue,
          id: itemId,
        }),
      );
    } catch (e) {
      console.warn('Failed to mount date picker', e);
    }
  });
};

export default DatePickerComponent;